import React, { useEffect, useState } from 'react';
import { IStepperProps } from '@components/Sura/Stepper/types';
import MobileStepper from '@material-ui/core/MobileStepper';
import useStyles from '@components/Sura/Stepper/styles';
import useStore from '@hooks/useStore';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Divider from '@material-ui/core/Divider';
import { Button, Grid } from '@material-ui/core';
import OysterButton from '@components/common/Button';

import PaymentMethods from '@components/Sura/Stepper/PaymentMethods';
import CardForm from '@components/Sura/Stepper/OnlinePayment/Card';
import { observer } from 'mobx-react';
import OnlinePayment from '@components/Sura/Stepper/OnlinePayment';
import { PaymentMethod, PaymentOption, Sources } from '@store/sura';
import { SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types';
import PaymentReceipt from '@components/Sura/PaymentReceipt';
import { initOpenPay } from '@utils/openPay';
import { isProd, porCobrarSuraAppUrl } from '@configs/variables';
import { Partner } from '@constants/paymentLink';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';
import SendReceipt from '@components/Sura/SendReceipt';
import ReferencedPayment from '@components/Sura/Stepper/ReferencedPayment';
import { validationPaymentMethod } from '@utils/suraValidation';
import { IPaymentMethod } from '@components/Sura/Stepper/types';
import MobilePaymentDetail from '@components/Sura/PaymentDetails/Mobile';
import PaymentExpired from '@components/Sura/PaymentExpired';

interface FormPayload {
  holderName: string;
  cardNumber: string;
  cardMonthExpiration: string;
  cardYearExpiration: string;
  cvv2: string;
  payeeEmail: string;
  payeePhone: string;
}
const limitAmount = 200000;

interface PaymentResponse {
  metadata: {
    redirectUrl: string;
  };
  success: boolean;
}

const Stepper: React.FC<IStepperProps> = ({ isPaymentSuccess = false }) => {
  const {
    sura,
    links,
    openPay,
    links: {
      paymentConfig,
      paymentLink: { paymentLinkId, exchangeRate },
    },
    checkout,
    ui,
  } = useStore();
  const { paymentMethods } = paymentConfig;
  const { setErrorMessage, errorMessage } = openPay;
  const { openToast } = useToast();

  const css = useStyles({ isMobile: ui.isMobileScreen });
  const methods = useForm<FormPayload>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      holderName: '',
      cardNumber: '',
      cardMonthExpiration: '',
      cardYearExpiration: '',
      cvv2: '',
      payeeEmail: '',
      payeePhone: '',
    },
  });
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const transactionId = urlParams.get('id');
  const suraSource = links.paymentLink.sura.source;

  const [checkedPrivacyNotice, setCheckedPrivacyNotice] = useState<boolean>(false);
  const [checkedSubscribeCard, setCheckedSubscribeCard] = useState<boolean>(false);
  const [checkedSendEmail, setCheckedSendEmail] = useState<boolean>(false);
  const [interestFreeMonths, setInterestFreeMonths] = useState<boolean>(false);
  const [optionSelectedMSI, setOptionSelectedMSI] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const isUSD = exchangeRate === 'USD';
  const isLimitAmountRached = Number(links.paymentLink.total) > limitAmount;
  const isOnlySubscription = links.paymentLink.sura.onlySubscription;
  const [isFormValid, setIsFormValid] = useState(false);
  const isAmexMode = sura.paymentOption === PaymentOption.american;
  const [hasFormErrors, setHasFormErrors] = useState(false);

  const handleBack = () => {
    console.log('sura.step', sura.step);
    if (sura.step === 4) {
      if (sura.paymentMethod === PaymentMethod.cash) {
        sura.setPaymentMethod('');
        window.history.pushState(null, '', sura.baseUrl);
        sura.setStep(0);
      } else if (sura.paymentMethod === PaymentMethod.bankReference) {
        window.history.pushState(null, '', `${sura.baseUrl}/bank-reference`);
        sura.setStep(1);
      } else if (sura.paymentMethod === 'send-reference') {
        sura.setPaymentMethod('');
        window.history.pushState(null, '', sura.baseUrl);
        sura.setStep(0);
      } else if (isPaymentSuccess) sura.setStep(3);
    } else if (sura.step === 1) {
      if (isOnlySubscription) {
        window.location.href =
          suraSource === Sources.portal_client
            ? `${porCobrarSuraAppUrl}/cobranza/recibos`
            : `${porCobrarSuraAppUrl}/cobranza/agente`;
      } else {
        sura.setPaymentMethod('');
        window.history.pushState(null, '', sura.baseUrl);
        sura.setStep(0);
      }
    } else if (sura.step === 2) {
      sura.setPaymentOption('');
      sura.setStep(1);
      if (sura.paymentMethod === PaymentMethod.card)
        window.history.pushState(null, '', `${sura.baseUrl}/online-payment`);
    } else if (sura.step === 0) {
      window.location.href =
        suraSource === Sources.portal_client
          ? `${porCobrarSuraAppUrl}/cobranza/recibos`
          : `${porCobrarSuraAppUrl}/cobranza/pago`;
    }
  };

  const onSubmit = async (params: FormPayload) => {
    setLoading(true);
    const name = params.holderName;
    const payeeFirstName = name.split(' ')[0] ?? '';
    const payeeLastName = name.split(' ')[1] ?? '';
    const expirationDate = `${params.cardMonthExpiration}/${params.cardYearExpiration}`;

    try {
      const result = await openPay.pay({
        ...params,
        paymentLinkId,
        expiration: expirationDate,
        payeeFirstName,
        payeeLastName,
        isSura: sura.isSura,
        subscribeCard: checkedSubscribeCard,
        ...(optionSelectedMSI !== '' && {
          interestFreeMonths: optionSelectedMSI,
        }),
        suraSubscriptionApiKeyRequired: true,
      });
      if (!result) {
        throw new Error('No se recibió respuesta del proceso de pago');
      }
      const { metadata, success } = result as PaymentResponse;
      const shouldSkip3DS = !metadata.redirectUrl && success;
      if (shouldSkip3DS) {
        setHasFormErrors(false);
        checkout.setCardPaymentSuccess();
      } else {
        window.location.href = metadata.redirectUrl;
      }
    } catch (e: any) {
      // OpenPay ya maneja los errores en el store
      setErrorMessage(e);
      setLoading(false);
    }
  };

  const confirm3ds = async (transactionId: string) => {
    try {
      const response = await openPay.confirm3dsPaymentStatus({
        partner: Partner.OPENPAY,
        transactionId,
      });
      if (response.status === 'COMPLETED') {
        setHasFormErrors(false);
        checkout.setCardPaymentSuccess();
      } else {
        openToast({
          message: response.errorMessage,
          title: 'Error',
          type: 'error',
        });
      }
    } catch (e: any) {
      console.log('confirm3ds error:', e);
      setErrorMessage(e?.error_code || e?.errors?.error_code || e);
      setLoading(false);
    }
  };

  const redictUrl = (method: IPaymentMethod) => {
    return !validationPaymentMethod(method) ? true : history.pushState(null, '', sura.baseUrl);
  };

  const validationButtonBack = () => {
    return (suraSource === Sources.share_email && sura.step > 1) ||
      suraSource !== Sources.share_email
      ? true
      : false;
  };

  const subscriptionSubmit = async (params: FormPayload) => {
    setLoading(true);
    const name = params.holderName;
    const payeeFirstName = name.split(' ')[0] ?? '';
    const payeeLastName = name.split(' ')[1] ?? '';
    const expirationDate = `${params.cardMonthExpiration}/${params.cardYearExpiration}`;

    try {
      const response = await openPay.createCardSubscription({
        ...params,
        paymentLinkId,
        expiration: expirationDate,
        payeeFirstName,
        payeeLastName,
        isSura: sura.isSura,
        suraSubscriptionApiKeyRequired: true,
      });
      setHasFormErrors(false);
      checkout.setSubscriptionSuccess();
    } catch (e: any) {
      // OpenPay ya maneja los errores en el store
      setLoading(false);
    }
  };

  const handleOnlySubscribeCard = () => {
    methods.handleSubmit(subscriptionSubmit as SubmitHandler<FieldValues>)();
  };

  useEffect(() => {
    if (errorMessage !== '') {
      setHasFormErrors(true);
      openToast({
        message: errorMessage,
        title: 'Error',
        type: 'error',
      });
    }
  }, [errorMessage]);

  useEffect(() => {
    if (transactionId) {
      if (location.href.includes('?')) {
        const newUrl = location.href.split('?')[0];
        newUrl.replace('methods', 'success');
        history.pushState({}, '', newUrl);
      }
      confirm3ds(transactionId);
    }
  }, [transactionId]);

  useEffect(() => {
    if (sura.step !== 2) {
      setCheckedPrivacyNotice(false);
      setHasFormErrors(false);
      methods.reset({
        holderName: '',
        cardNumber: '',
        cardMonthExpiration: '',
        cardYearExpiration: '',
        cvv2: '',
        payeeEmail: '',
        payeePhone: '',
      });
    }
  }, [sura.step]);

  useEffect(() => {
    if (paymentMethods && paymentMethods.card.isPartnerOpenPay) {
      initOpenPay({
        apiKey: paymentMethods.card.publicApiKey,
        id: paymentMethods.card.merchantId,
        isProd,
      });
    }
  }, [paymentMethods.card.publicApiKey, paymentMethods.card.merchantId]);

  useEffect(() => {
    if (
      (suraSource === Sources.share_email && !isUSD && !isLimitAmountRached) ||
      isOnlySubscription
    ) {
      sura.setStep(1);
      sura.setPaymentMethod(PaymentMethod.card);
      history.pushState(null, '', `${sura.baseUrl}/online-payment`);
    }
  }, [isOnlySubscription, suraSource, history]);

  useEffect(() => {
    if (
      location.href.includes('/online-payment/card') &&
      redictUrl(paymentMethods.card) &&
      !isUSD &&
      !isLimitAmountRached
    ) {
      sura.setStep(2);
      sura.setPaymentMethod(PaymentMethod.card);
      if (location.href.includes('/visa-mastercard'))
        sura.setPaymentOption(PaymentOption.visa_mastercard);
      if (location.href.includes('/amex')) sura.setPaymentOption(PaymentOption.american);
    } else if (
      location.href.includes('/online-payment/direct-debit') &&
      !isUSD &&
      !isLimitAmountRached
    ) {
      sura.setStep(2);
      sura.setPaymentMethod(PaymentMethod.card);
      sura.setPaymentOption(PaymentOption.clabe);
    } else if (location.href.includes('/online-payment') && !isUSD && !isLimitAmountRached) {
      sura.setStep(1);
      sura.setPaymentMethod(PaymentMethod.card);
    } else if (location.href.includes('/shared')) {
      sura.setStep(4);
      sura.setSendReceipt(true);
    } else if (location.href.includes('/bank-reference/send-reference')) {
      sura.setStep(4);
      sura.setSendReceipt(true);
      sura.setPaymentMethod(PaymentMethod.bankReference);
      sura.setPaymentOption(PaymentOption.send_reference);
    } else if (location.href.includes('/bank-reference')) {
      sura.setStep(1);
      sura.setPaymentMethod(PaymentMethod.bankReference);
    } else if (location.href.includes('/send-reference')) {
      sura.setStep(4);
      sura.setPaymentMethod('send-reference');
      sura.setSendReceipt(true);
    } else {
      history.pushState(null, '', sura.baseUrl);
    }
  }, [history]);

  useEffect(() => {
    const checkFormValidity = () => {
      const values = methods.getValues();
      const isCvvValid = () => {
        const cvv = values.cvv2 || '';
        return isAmexMode ? cvv.length === 4 : cvv.length === 3;
      };

      const hasAllValues = Boolean(
        values.holderName &&
          values.cardNumber &&
          values.cardMonthExpiration &&
          values.cardYearExpiration &&
          values.payeeEmail &&
          values.payeePhone &&
          isCvvValid(),
      );

      setIsFormValid(hasAllValues);
    };

    const subscription = methods.watch(() => {
      checkFormValidity();
    });

    checkFormValidity();

    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [methods, isAmexMode, hasFormErrors]);

  return (
    <Grid>
      <FormProvider {...methods}>
        <form>
          {sura.step !== 3 && (
            <>
              <Grid className={css.divHeader} xs={12}>
                <Grid
                  className={!isOnlySubscription ? css.divBack : css.divBackSubscription}
                  item
                  xs={!isOnlySubscription ? 3 : 12}
                >
                  {validationButtonBack() && (
                    <Button
                      className={!isOnlySubscription ? css.labelBack : css.labelBackSubscription}
                      onClick={handleBack}
                      startIcon={<ArrowBackIcon className={css.arrowBackIcon} />}
                    >
                      {!isOnlySubscription ? (
                        'Atrás'
                      ) : (
                        <label className={css.labelPayment}>Domiciliación de Póliza</label>
                      )}
                    </Button>
                  )}
                </Grid>
                {!isOnlySubscription && (
                  <Grid className={css.divStepper} item xs={9}>
                    <Grid className={css.gridStepper}>
                      {sura.step <= 3 && (
                        <MobileStepper
                          variant="dots"
                          steps={
                            sura.paymentMethod === PaymentMethod.bankReference ||
                            isUSD ||
                            isLimitAmountRached
                              ? 2
                              : suraSource === Sources.share_email || isOnlySubscription
                              ? 3
                              : 4
                          }
                          position="static"
                          activeStep={
                            suraSource === Sources.share_email || isOnlySubscription
                              ? sura.step - 1
                              : sura.step
                          }
                          classes={{
                            root: css.root,
                            dots: css.dots,
                            dot: css.dot,
                            dotActive: css.dotActive,
                          }}
                          backButton={undefined}
                          nextButton={undefined}
                        />
                      )}
                    </Grid>
                    <Grid>
                      {sura.step === 2 &&
                        (sura.paymentOption === PaymentOption.visa_mastercard ||
                          sura.paymentOption === PaymentOption.american ||
                          sura.paymentOption === PaymentOption.clabe) && (
                          <OysterButton
                            label="Pagar"
                            loading={loading}
                            onClick={methods.handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
                            className={css.payButton}
                            disabled={
                              !checkedPrivacyNotice || !checkedSendEmail || loading || !isFormValid
                            }
                          />
                        )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Divider variant="inset" className={css.divider} />
            </>
          )}
          {sura.step < 3 && ui.isMobileScreen && !isOnlySubscription && <MobilePaymentDetail />}
          {sura.step === 0 && <PaymentMethods />}
          {sura.step === 1 && sura.paymentMethod === PaymentMethod.card && <OnlinePayment />}
          {sura.step === 1 && sura.paymentMethod === PaymentMethod.bankReference && (
            <ReferencedPayment />
          )}
          {sura.step === 2 &&
            (sura.paymentOption === PaymentOption.visa_mastercard ||
              sura.paymentOption === PaymentOption.american ||
              sura.paymentOption === PaymentOption.clabe) && (
              <CardForm
                checkedPrivacyNotice={checkedPrivacyNotice}
                setCheckedPrivacyNotice={setCheckedPrivacyNotice}
                checkedSubscribeCard={checkedSubscribeCard}
                setCheckedSubscribeCard={setCheckedSubscribeCard}
                errorFields={
                  hasFormErrors
                    ? {
                        cardNumber: true,
                        cardMonthExpiration: true,
                        cardYearExpiration: true,
                        cvv2: true,
                        clabeAccount: true,
                        bankName: true,
                      }
                    : {}
                }
                interestFreeMonths={interestFreeMonths}
                setInterestFreeMonths={setInterestFreeMonths}
                optionSelectedMSI={optionSelectedMSI}
                setOptionSelectedMSI={setOptionSelectedMSI}
                handleOnlySubscribeCard={handleOnlySubscribeCard}
                checkedSendEmail={checkedSendEmail}
                setCheckedSendEmail={setCheckedSendEmail}
                loading={loading}
                methods={methods}
              />
            )}
        </form>
      </FormProvider>
      {sura.step === 3 && !sura.isPaymentExpired && <PaymentReceipt />}
      {sura.step === 3 && sura.isPaymentExpired && <PaymentExpired />}
      {sura.sendReceipt && sura.step === 4 && (
        <SendReceipt
          title={
            sura.paymentMethod === PaymentMethod.bankReference
              ? 'Recibo para pagos referenciados'
              : sura.paymentMethod === 'send-reference'
              ? 'Compartir liga de pago'
              : 'Enviar comprobante de pago'
          }
        />
      )}
    </Grid>
  );
};

export default observer(Stepper);
